import { useTranslation } from 'react-i18next';

import { AccountLayout } from '@components/layout/AccountLayout';

function StatGhost({ bars = false }: { bars?: boolean }) {
  return (
    <div className="rounded-2xl border border-border bg-white/60 p-5">
      <div className="mb-3 h-2 w-16 rounded-full bg-muted/60" />
      {bars ? (
        <div className="flex h-10 items-end gap-1">
          {[55, 80, 45, 70, 60, 90, 50].map((h, i) => (
            <div
              key={i}
              className="flex-1 rounded-sm bg-primary/20"
              style={{ height: `${h}%` }}
            />
          ))}
        </div>
      ) : (
        <>
          <div className="mb-2 h-7 w-20 rounded-lg bg-muted/50" />
          <div className="h-2 w-12 rounded-full bg-muted/40" />
        </>
      )}
    </div>
  );
}

function ChartGhost() {
  return (
    <div className="rounded-2xl border border-border bg-white/60 p-6">
      <div className="mb-1 h-2.5 w-28 rounded-full bg-muted/60" />
      <div className="mb-5 h-2 w-16 rounded-full bg-muted/40" />
      <div className="flex h-28 items-end gap-1.5">
        {[40, 65, 50, 80, 45, 72, 38, 90, 60, 75, 55, 82].map((h, i) => (
          <div
            key={i}
            className="flex-1 rounded-t-sm bg-primary/20"
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
    </div>
  );
}

function ListGhost() {
  return (
    <div className="rounded-2xl border border-border bg-white/60 p-6">
      <div className="mb-4 h-2.5 w-24 rounded-full bg-muted/60" />
      <div className="space-y-3">
        {[80, 65, 50, 40].map((w, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="h-7 w-7 shrink-0 rounded-full bg-muted/40" />
            <div className="flex-1 space-y-1.5">
              <div className="h-2 rounded-full bg-muted/50" style={{ width: `${w}%` }} />
              <div className="h-2 w-1/3 rounded-full bg-muted/30" />
            </div>
            <div className="h-2 w-10 rounded-full bg-muted/40" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function DashboardPage() {
  const { t } = useTranslation();

  return (
    <AccountLayout>
      <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden" style={{ background: 'radial-gradient(ellipse 80% 60% at 60% -10%, hsl(160 51% 24% / 0.08) 0%, transparent 70%), radial-gradient(ellipse 60% 50% at -10% 80%, hsl(160 51% 24% / 0.06) 0%, transparent 65%), hsl(var(--color-background))' }}>

        {/* Decorative ring ornament — top right */}
        <div
          className="pointer-events-none absolute -right-28 -top-28 text-primary"
          aria-hidden="true"
        >
          <svg width="520" height="520" viewBox="0 0 520 520" fill="none">
            <circle cx="260" cy="260" r="220" stroke="currentColor" strokeWidth="1" opacity="0.08" />
            <circle cx="260" cy="260" r="170" stroke="currentColor" strokeWidth="1" opacity="0.07" />
            <circle cx="260" cy="260" r="120" stroke="currentColor" strokeWidth="1.5" opacity="0.06" />
            <circle cx="260" cy="260" r="70"  stroke="currentColor" strokeWidth="2"   opacity="0.05" />
          </svg>
        </div>

        {/* Decorative ring ornament — bottom left */}
        <div
          className="pointer-events-none absolute -bottom-44 -left-44 text-primary"
          aria-hidden="true"
        >
          <svg width="440" height="440" viewBox="0 0 440 440" fill="none">
            <circle cx="220" cy="220" r="180" stroke="currentColor" strokeWidth="1" opacity="0.06" />
            <circle cx="220" cy="220" r="130" stroke="currentColor" strokeWidth="1" opacity="0.05" />
            <circle cx="220" cy="220" r="80"  stroke="currentColor" strokeWidth="1.5" opacity="0.04" />
          </svg>
        </div>

        {/* Page content */}
        <div className="relative mx-auto flex max-w-3xl flex-col items-center px-4 pb-20 pt-16 sm:pt-20">

          {/* Brand icon mark */}
          <div className="mb-6 flex h-[72px] w-[72px] items-center justify-center rounded-2xl bg-primary shadow-md">
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true">
              <rect x="3"  y="3"  width="12" height="12" rx="2.5" fill="white" />
              <rect x="21" y="3"  width="12" height="12" rx="2.5" fill="white" />
              <rect x="3"  y="21" width="12" height="12" rx="2.5" fill="white" />
              <rect x="21" y="21" width="12" height="12" rx="2.5" fill="white" opacity="0.4" />
            </svg>
          </div>

          {/* Coming soon badge */}
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-1.5">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
            <span className="text-xs font-semibold uppercase tracking-widest text-primary">
              {t('layout.account.comingSoon')}
            </span>
          </div>

          {/* Title */}
          <h1 className="mb-3 font-display text-5xl font-semibold text-foreground sm:text-6xl">
            Dashboard
          </h1>

          {/* Description */}
          <p className="mb-14 max-w-xs text-center text-base leading-relaxed text-muted-foreground">
            Your analytics hub is on its way. We're building something great.
          </p>

          {/* Ghost widgets — hint at the future layout */}
          <div className="w-full space-y-3 opacity-40">
            {/* Stat row */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatGhost />
              <StatGhost />
              <StatGhost bars />
              <StatGhost />
            </div>

            {/* Chart */}
            <div className="opacity-75">
              <ChartGhost />
            </div>

            {/* List + stat pair */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 opacity-75">
              <ListGhost />
              <div className="space-y-3">
                <StatGhost />
                <StatGhost bars />
              </div>
            </div>
          </div>
        </div>
      </div>
    </AccountLayout>
  );
}
